import { useId, type HTMLAttributes } from 'react';
import { cx } from './cx.js';
import { FieldLabel } from './FieldLabel.js';
import { Select, type SelectOption } from './Select.js';

/**
 * A setting with three or four answers, picked from a list.
 *
 * The row is the same row a `Switch` draws: a name on the left, an optional
 * line of hint under it, the control on the right. Only the control differs,
 * because the question does. On or off is a switch; the deck style and the bot
 * speed are a short list, and a short list is a `Select`.
 *
 * This sat inside `SettingsList.tsx` with `Switch` and `Section`, and
 * `/dev/lookbook` imported all three out of that feature file to demo them. The
 * design system was describing itself by reaching into a screen.
 *
 * What it supplies that a caller would forget is the name. The dropdown inside
 * has no label element of its own, and it can no longer take one: `Select` is a
 * `role="combobox"` button now rather than a `<select>`, and a `<label>` wrapped
 * around a combobox names it and then hides its value, because a combobox's
 * value is its content. So the row owns an id, hangs it on the name, and hands
 * it over as `aria-labelledby` — the dropdown joins it to its own value and a
 * reader announces "Bot pace, Natural". Write the name beside a bare `Select`
 * instead and you get a dropdown a screen reader announces as nothing at all.
 *
 * The row is a `<div>` for the same reason. It was a `<label>`, which is what
 * made the whole row a tap target for the control inside it; a label wrapped
 * around a button forwards a press to it, so the row would have opened the menu
 * from anywhere along its width, including the hint. The control is the target
 * now, and it is already 48px tall.
 */

export type ChoiceOption = SelectOption;

export interface ChoiceProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'className' | 'style' | 'children' | 'onChange'
> {
  /** What the setting is called. Already translated. */
  readonly label: string;
  /** One line under it, for a setting whose name is not the whole story. */
  readonly hint?: string | undefined;
  readonly value: string;
  readonly options: readonly ChoiceOption[];
  readonly onChange: (next: string) => void;
  /** Layout and state only. The row and the dropdown are not yours. */
  readonly className?: string | undefined;
}

const ROW = 'flex min-h-13 items-center gap-4 py-2 text-ink';

export function Choice({
  label,
  hint,
  value,
  options,
  onChange,
  className,
  ...rest
}: ChoiceProps): React.JSX.Element {
  const nameId = useId();
  return (
    <div className={cx(ROW, className)} {...rest}>
      <FieldLabel id={nameId} className="flex-1" hint={hint}>
        {label}
      </FieldLabel>
      <Select
        fullWidth={false}
        className="min-w-38 shrink-0"
        aria-labelledby={nameId}
        value={value}
        options={options}
        onChange={onChange}
      />
    </div>
  );
}
